// Every OGS URL kill-switch resolved in ONE pure place, so a flag's meaning is
// readable here rather than by grepping scattered URLSearchParams sites. Pure +
// injectable so the combination gates are unit-tested without a browser.
//
// Documented in CLAUDE.md ("OGS splat inference"): ?ep, ?gpubuf, ?gpulift,
// ?gpubuflift, ?gpuplucker, ?gpudeform, ?fp16, ?geomfp16, ?apprfp16,
// ?rigclamp, ?rigclamppad, ?meshview, ?gaze, ?blink.
// All default ON; "0" is the only value that turns one off. ?rig and ?myralift
// are the exceptions — three-way CHOICES, see RigMode and MyraLift.

/** Which rig→vertices implementation a branch is built from.
 *
 *  Not a boolean, because the two are not "a feature and its absence": they are
 *  two rigs, and the interesting operation is comparing them on the same
 *  character. `?gpudeform` still picks CPU vs GPU underneath whichever wins.
 *
 *   - `auto` (default): the distilled rig2mesh, which is the default FOR NOW
 *     while the exact rig is proven on real characters — so a character keeps
 *     the rig it has today. It takes the exact rig only where a bundle declares
 *     no rig2mesh this app can load, since there the alternative is a frozen
 *     head. Costs no extra bytes: only one of the two is ever fetched.
 *   - `orl`: the exact MetaHuman rig. This is how it gets driven and judged
 *     before it becomes the default. A character with no pack renders a FROZEN
 *     neutral head and says so, rather than quietly handing back the other rig.
 *   - `rig2mesh`: force the distillation even where a bundle carries only a
 *     pack. The ORL assets are not fetched; every declared rig2mesh is. */
export type RigMode = "auto" | "orl" | "rig2mesh";

/** Shared `GpuLifter` or the myra fork; `auto` picks by subject (myraLiftGate.ts).
 *  A CHOICE like RigMode, so the two lifters can be A/B'd on one character. */
export type MyraLift = "auto" | "on" | "off";

/** How far the live ARKit path may drive a control.
 *
 *  A three-way CHOICE, not a switch, because "off" is two different things and
 *  conflating them is what made the original `?rigclamp=0` ambiguous:
 *
 *   - `trained` (default): the per-control box the bundle's pose table actually
 *     visited (`trainedLimits`). A single ARKit channel at 1.0 can otherwise sit
 *     5-7x past anything the decoders saw, and they extrapolate into the
 *     blown-out, glowing face. The raw rig board has clamped here since ENG-946,
 *     so this is also what keeps the two UIs from disagreeing about the same pose.
 *   - `declared`: the bundle's nominal `rig_range` only ([0, 1] where none is
 *     declared). What the ARKit path did before ENG-946 — and the A/B that says
 *     how much expression the trained box is costing on a given bundle. On
 *     myragaussainsplatting the trained box keeps only 40% of the median
 *     channel's travel, because its 30-pose capture never went far.
 *   - `none`: no clamp at all. Diagnostic. The decoders WILL extrapolate.
 *
 *  Off by amount, not by flag: `?rigclamppad` widens the trained box instead of
 *  abandoning it, which is usually the knob someone actually wants. */
export type RigClamp = "trained" | "declared" | "none";

/** `?meshview` — draw the branch GEOMETRY instead of (or beside) the Gaussians.
 *
 *  `off` (default) | `wire` (`?meshview=1` too) | `solid` | `both` (geometry over
 *  the splats). The splat render cannot answer "is the RIG moving the mesh",
 *  because every part of it — offsets, scales, opacities, colour — is decoder
 *  output that responds to the control vector on its own. Drawing the posed mesh
 *  answers it directly. */
export type MeshView = "off" | "wire" | "solid" | "both";

export interface OgsGpuFlags {
  /** ?ep=wasm|webgpu — explicit EP override, else null (auto-detect). */
  readonly ep: string | null;
  /** ?gpubuf — keep the `code` latent on the shared device through geom→appr. */
  readonly gpuBuf: boolean;
  /** ?gpulift — WGSL GpuLifter (else the CPU GaussianLifter). */
  readonly gpuLift: boolean;
  /** ?gpubuflift — geom_uv/color_uv emitted as gpu-buffers straight into the
   *  lifter. Implies gpuBuf + gpuLift: without the shared device or the GPU
   *  lifter there is nothing to hand a GPUBuffer to. */
  readonly gpuBufLift: boolean;
  /** ?gpuplucker — plücker rays computed in WGSL on the shared device. */
  readonly gpuPlucker: boolean;
  /** ?gpudeform — the exact rig's WGSL deform runs on the lift device and its
   *  posed `vertices` stay gpu-resident into setPoseGpu. Off = CPU deform. */
  readonly gpuDeform: boolean;
  /** ?fp16 — master switch for the fp16-internal decoders. */
  readonly fp16: boolean;
  /** ?geomfp16 — per-decoder fp16 switch (AND-ed with fp16). */
  readonly geomFp16: boolean;
  /** ?apprfp16 — per-decoder fp16 switch (AND-ed with fp16). */
  readonly apprFp16: boolean;
  /** ?rig=auto|orl|rig2mesh — which rig drives the branches. See RigMode. */
  readonly rig: RigMode;
  /** ?myralift=0|1 — force the shared / the myra lift; absent = by subject. */
  readonly myraLift: MyraLift;
  /** ?rigclamp=trained|declared|none — how far the live ARKit path may drive a
   *  control. `0` is accepted as the historical spelling of `declared`. */
  readonly rigClamp: RigClamp;
  /** ?rigclamppad=<v> — widen the trained box by this much on each side (the
   *  `pad` argument to trainedLimits, default 0.03). Null = the default. The
   *  measured, reversible way to buy amplitude on a bundle whose capture was
   *  narrow, as against dropping the clamp outright. */
  readonly rigClampPad: number | null;
  /** ?rigconstrain=<0..1> — how much of the trained-domain CLAMP and the trained
   *  SUBSPACE projection reaches the rig. 1 (the default) is the shipped, fully
   *  constrained face; 0 is the same rig with neither applied, i.e. exactly what
   *  `?rigclamp=none&rigpca=off` produces; a value in between renders the linear
   *  blend of the two.
   *
   *  It exists because the two constraints are the difference between a face that
   *  cannot over-respond and a face that keeps its amplitude, and on a given bundle
   *  the right answer sits BETWEEN them — the existing knobs can only take each
   *  stage all the way off, which is a different question ("what is this stage
   *  doing?") from "how much of it does this character want?". Blending the two
   *  RESULTS rather than loosening either stage keeps both measured: the loose twin
   *  is the raw ARKit-composed rig and the tight one is the shipped pipeline, so no
   *  intermediate value invents a constraint neither arm applies.
   *
   *  It does NOT blend the other rig stages — the live mouth mix (?livemouth), the
   *  blink macro (?blinkmacro) and the region mask (?regionmask) are DRIVES, not
   *  constraints, and each has its own switch. */
  readonly rigConstrain: number | null;
  /** ?meshview=wire|solid|both — draw the posed branch meshes. See MeshView.
   *  Forces the CPU deform (below), because that is the only path the posed
   *  vertices reach the CPU on. */
  readonly meshView: MeshView;
  /** ?gaze=0 — stop the eye-contact drive writing the aim controls on the splat
   *  face path.
   *
   *  It is the EYE REGION, not the eye contact feature, that some bundles cannot
   *  take. `applyEyeFollowRig`'s direct mode writes the six aim controls linearly
   *  and clamps only to the DECLARED range, so on a bundle whose capture never
   *  contained that configuration the decoders extrapolate — measured on
   *  myragaussainsplatting, gaze alone takes the bright-pixel count from 22.5k to
   *  100.7k and the head renders as a glowing smear, from ~0.15 upward on
   *  controls whose trained maxima are 0.43-0.69. A bundle that ships
   *  `mouth_controller.eyes_idx` gets the TRAINED gaze drive instead and needs
   *  none of this. */
  readonly gaze: boolean;
  /** ?blink=0 — stop the procedural blink reaching the splat face rig.
   *
   *  The same region and the same failure, transient rather than continuous: the
   *  macro drives 14 lid controls toward a trained closed row, and on the bundle
   *  above that flashes once per blink (11 flashes in 12 s at the shipped 1 s
   *  interval, peak 12k bright px above the head; 0 with blinks suppressed).
   *  Note the blink NODE's weight is graph-driven, so this is the only switch
   *  that stops it without editing the graph. */
  readonly blink: boolean;
}

// A flag is ON unless its param is exactly "0" (matches every existing gate).
function on(p: URLSearchParams, key: string): boolean {
  return p.get(key) !== "0";
}

// An unrecognised ?rig= value falls back to `auto` and SAYS so. Silently
// treating `?rig=ORL` or `?rig=exact` as the default would leave someone
// comparing two runs that are in fact the same run.
function rigMode(p: URLSearchParams): RigMode {
  const v = p.get("rig");
  if (v === null) return "auto";
  if (v === "auto" || v === "orl" || v === "rig2mesh") return v;
  console.warn(`[ogs] ?rig=${v} is not one of auto|orl|rig2mesh — using auto`);
  return "auto";
}

// Same refusal-to-guess as rigMode: an unrecognised value would A/B a run against itself.
function myraLift(p: URLSearchParams): MyraLift {
  const v = p.get("myralift");
  if (v === null) return "auto";
  if (v === "0") return "off";
  if (v === "1") return "on";
  console.warn(`[ogs] ?myralift=${v} is not 0 or 1 — picking the lift by subject`);
  return "auto";
}

// Same refusal-to-guess as rigMode. `0` is kept as an alias for `declared`
// because that is what the flag meant before it was deleted, and a stale URL
// must not silently mean something else.
function rigClamp(p: URLSearchParams): RigClamp {
  const v = p.get("rigclamp");
  if (v === null) return "trained";
  if (v === "trained" || v === "declared" || v === "none") return v;
  if (v === "0") return "declared";
  console.warn(`[ogs] ?rigclamp=${v} is not one of trained|declared|none — using trained`);
  return "trained";
}

// Out of range or unparseable falls back to 1 — the shipped, constrained face —
// and says so. Defaulting a bad value to 0 would silently ship an unconstrained
// rig off a typo, which is the failure resolveRigProjection refuses for the same
// reason.
// NULL WHEN NOT TYPED, so the DEFAULT lives in exactly one place
// (faceRigDefaults.DEFAULT_FACE_RIG_WEIGHT, which the arm applies) instead of
// being copied into this resolver, where it would silently disagree the day the
// canonical one moves. A BAD value resolves the same way and says so --
// defaulting a typo to 0 would ship an unconstrained rig, which is the failure
// resolveRigProjection refuses for the same reason.
function rigConstrain(p: URLSearchParams): number | null {
  const v = p.get("rigconstrain");
  if (v === null) return null;
  const n = Number(v);
  if (Number.isFinite(n) && n >= 0 && n <= 1) return n;
  console.warn(`[ogs] ?rigconstrain=${v} is not a number in [0, 1] — using the default`);
  return null;
}

// A pad is a WIDTH, so a negative one (a tighter box than the data) is refused
// rather than clamped to 0 — it would be a request nobody could have meant.
function rigClampPad(p: URLSearchParams): number | null {
  const v = p.get("rigclamppad");
  if (v === null) return null;
  const n = Number(v);
  if (Number.isFinite(n) && n >= 0) return n;
  console.warn(`[ogs] ?rigclamppad=${v} is not a number >= 0 — using the default pad`);
  return null;
}

// `1` means the wireframe: it is the view someone typing ?meshview=1 wants
// (the splats stay hidden, the lines show the mesh), and `both` is the deliberate
// overlay. An unrecognised value would silently show nothing, so it warns.
function meshView(p: URLSearchParams): MeshView {
  const v = p.get("meshview");
  if (v === null || v === "0") return "off";
  if (v === "1" || v === "wire") return "wire";
  if (v === "solid" || v === "both") return v as MeshView;
  console.warn(`[ogs] ?meshview=${v} is not one of wire|solid|both — drawing the wireframe`);
  return "wire";
}

/** Resolve the flags from a query string ("?a=b" or "a=b"). Pure. */
export function resolveOgsGpuFlags(search = ""): OgsGpuFlags {
  const p = new URLSearchParams(search);
  const gpuBuf = on(p, "gpubuf");
  const gpuLift = on(p, "gpulift");
  const fp16 = on(p, "fp16");
  const view = meshView(p);
  return {
    ep: p.get("ep"),
    gpuBuf,
    gpuLift,
    // Combination gate — see the field doc.
    gpuBufLift: on(p, "gpubuflift") && gpuBuf && gpuLift,
    gpuPlucker: on(p, "gpuplucker"),
    // Combination gate: the mesh view reads `branch.verts`, which only the CPU
    // deform writes — on the GPU path the posed vertices go straight to the
    // lifter as a GPUBuffer and that array stays at its neutral pose. A mesh view
    // that silently showed the neutral mesh would answer the question it exists
    // to ask with a confident lie.
    gpuDeform: on(p, "gpudeform") && view === "off",
    fp16,
    geomFp16: fp16 && on(p, "geomfp16"),
    apprFp16: fp16 && on(p, "apprfp16"),
    rig: rigMode(p),
    myraLift: myraLift(p),
    meshView: view,
    rigClamp: rigClamp(p),
    rigClampPad: rigClampPad(p),
    rigConstrain: rigConstrain(p),
    gaze: on(p, "gaze"),
    blink: on(p, "blink"),
  };
}

/** Which source decided each eye-region drive. `url` means someone typed the flag, and
 *  it outranks the bundle in BOTH directions — `?gaze=1` on a bundle that declares gaze
 *  off is a deliberate look at what the declaration is suppressing. */
export type FaceDriveKey = "gaze" | "blink" | "brow" | "eye" | "cheek" | "nose";

/** Every drive a bundle may decline. Two kinds, deliberately in ONE list because they
 *  are one question — what can this capture take:
 *    gaze, blink  procedural drives we generate
 *    brow, eye,   ARKit channel GROUPS, held at rest when declined (maskArkitRegions);
 *    cheek, nose  a thin pose table leaves these regions unconstrained by the subspace
 *                 fit, and they are where the face blows out. */
export const FACE_DRIVE_KEYS: readonly FaceDriveKey[] = ["gaze", "blink", "brow", "eye", "cheek", "nose"];

export type FaceDriveSources = Record<FaceDriveKey, "url" | "bundle" | "default">;

export type FaceDrives = Record<FaceDriveKey, boolean> & { readonly source: FaceDriveSources };

/**
 * The two eye-region drives, resolved URL > bundle > default(on).
 *
 * `?gaze` / `?blink` are session knobs, but whether a bundle can TAKE either is a
 * property of its capture (see SceneManifest.faceDrives) — the same answer for everyone
 * who opens that character, which is not something each viewer should have to know to
 * type. So a bundle may decline a drive and an untouched URL honours it.
 *
 * The bundle only speaks where the URL is silent, and only where the bundle itself
 * declared something: a key it omits leaves the drive ON, exactly as before.
 */
export function resolveFaceDrives(
  search = "",
  bundle?: Partial<Record<FaceDriveKey, boolean>> | null,
): FaceDrives {
  const p = new URLSearchParams(search);
  const out = {} as Record<string, unknown>;
  const source = {} as Record<string, string>;
  for (const key of FACE_DRIVE_KEYS) {
    if (p.has(key)) { out[key] = on(p, key); source[key] = "url"; continue; }
    const declared = (bundle as Record<string, unknown> | null | undefined)?.[key];
    if (typeof declared === "boolean") { out[key] = declared; source[key] = "bundle"; continue; }
    out[key] = true; source[key] = "default";
  }
  out.source = source;
  return out as unknown as FaceDrives;
}

/** The live flags for this page (defaults outside a browser — tests/SSR). */
export function ogsGpuFlags(): OgsGpuFlags {
  return resolveOgsGpuFlags(typeof window === "undefined" ? "" : window.location.search);
}
